'use client';

import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
    EnterAuthProvider,
    prewarmRiskSession,
    useEnterAuth,
    type EnterAuthConfig,
    type FingerprintAgentLoadTelemetry,
} from '@enter/auth';
import { getEnterAuthConfig } from '@/contexts/auth/enter-auth-config';
import { useMixpanel } from '@/contexts/mixpanel';

/**
 * 登录安全预热不属于分析采集，不受 Cookie 授权约束；但它要做一次设备识别，
 * 只对还没登录的访客有意义。产品页加载频繁，已登录用户不再重复预热；
 * 交互式登录本身仍会在跳转前做风险预检。
 */
function AnonymousRiskPrewarm({ config }: { config: EnterAuthConfig }) {
    const { status } = useEnterAuth();
    useEffect(() => {
        if (status === 'anonymous') void prewarmRiskSession(config);
    }, [config, status]);
    return null;
}

export function AuthProvider({
    children,
    getInviteCode,
    returnTo,
}: {
    /** 应用子树。 */ children: ReactNode;
    /** 由 App 注入邀请码输入，Auth 不读取邀请业务状态。 */ getInviteCode?: EnterAuthConfig['getInviteCode'];
    /** 未显式指定时的登录去向；缺省表示回到当前 URL。 */ returnTo?: string;
}) {
    const { logger } = useMixpanel();
    const reportFingerprintAgentLoad = useCallback(
        (telemetry: FingerprintAgentLoadTelemetry) => {
            logger(
                'auth_fingerprintjs_load',
                {
                    auth_trace_id: telemetry.authTraceId,
                    duration_ms: telemetry.durationMs,
                    result: telemetry.result,
                    trigger: telemetry.trigger,
                },
                { sendImmediately: true },
            );
        },
        [logger],
    );
    const enterAuthConfig = useMemo(
        () => ({
            ...getEnterAuthConfig(getInviteCode),
            onFingerprintAgentLoad: reportFingerprintAgentLoad,
        }),
        [getInviteCode, reportFingerprintAgentLoad],
    );
    const config = useMemo(() => ({ risk: enterAuthConfig, returnTo }), [enterAuthConfig, returnTo]);
    return (
        <EnterAuthProvider config={config}>
            <AnonymousRiskPrewarm config={enterAuthConfig} />
            {children}
        </EnterAuthProvider>
    );
}
